// frontend/src/pages/ResetPassword.jsx
import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "../axiosConfig";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();

  // mode: 'idle' | 'loading' | 'success' | 'error'
  const [mode, setMode] = useState("idle");
  const [message, setMessage] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  const isSubmitting = mode === "loading";
  const canSubmit = !isSubmitting && password.length >= 8 && confirm.length >= 8;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setMode("error");
      setMessage("Invalid link. Token is missing.");
      return;
    }
    if (password !== confirm) {
      setMode("error");
      setMessage("Passwords don't match.");
      return;
    }
    if (password.length < 8) {
      setMode("error");
      setMessage("Password must be at least 8 characters.");
      return;
    }

    setMode("loading");
    setMessage("");

    try {
      // IMPORTANT: keep relative path so axios baseURL applies
      await axios.post("reset-password", { token, newPassword: password });

      setMode("success");
      setMessage("Password reset! Redirecting to login…");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      setMode("error");
      setMessage(err?.response?.data?.error || "Reset failed. Please try again.");
    }
  };

  const matchState =
    password && confirm ? (password === confirm ? "match" : "mismatch") : "empty";

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 px-3 sm:px-4 py-6">
      {/* Dark-mode autofill contrast fix */}
      <style>{`
        .dark input:-webkit-autofill,
        .dark input:-webkit-autofill:focus,
        .dark input:-webkit-autofill:hover {
          -webkit-text-fill-color: #e5e7eb;
          transition: background-color 5000s ease-in-out 0s;
          caret-color: #e5e7eb;
        }
        .dark input:-webkit-autofill::first-line { color: #e5e7eb; }
      `}</style>

      <div className="w-full max-w-sm sm:max-w-md bg-white dark:bg-slate-900/95 border border-gray-200 dark:border-slate-700 rounded-2xl shadow-lg p-4 sm:p-6 md:p-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-center text-indigo-700 dark:text-indigo-300 mb-1.5 sm:mb-2">
          Reset Password
        </h1>
        <p className="text-center text-gray-600 dark:text-gray-300 text-xs sm:text-sm mb-4">
          Enter a new password for your account.
        </p>

        {/* Alerts */}
        {mode === "success" && (
          <div
            className="mb-4 rounded-md border border-green-200 bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800/40 p-2.5 text-xs sm:text-sm"
            role="alert"
          >
            {message}
          </div>
        )}
        {mode === "error" && (
          <div
            className="mb-4 rounded-md border border-red-200 bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800/40 p-2.5 text-xs sm:text-sm"
            role="alert"
          >
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
          {/* New Password */}
          <div>
            <label className="block text-gray-800 dark:text-gray-200 text-xs sm:text-sm mb-1">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="w-full text-sm sm:text-base px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800/80 text-gray-900 dark:text-slate-100 placeholder-gray-500 outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-500 focus:border-indigo-400 dark:focus:border-indigo-500 transition"
                placeholder="At least 8 characters"
                aria-describedby="password-help"
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                className="absolute inset-y-0 right-0 px-3 sm:px-4 text-xs sm:text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                aria-label={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? "Hide" : "Show"}
              </button>
            </div>
            <p id="password-help" className="mt-1 text-[11px] sm:text-xs text-gray-600 dark:text-gray-400">
              Use 8+ characters. A mix of letters, numbers, and symbols is recommended.
            </p>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-gray-800 dark:text-gray-200 text-xs sm:text-sm mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPw ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
                className={`w-full text-sm sm:text-base px-3 sm:px-4 py-2.5 sm:py-3 border rounded-lg bg-white dark:bg-slate-800/80 text-gray-900 dark:text-slate-100 placeholder-gray-500 outline-none focus:ring-2 transition
                  ${
                    matchState === "mismatch"
                      ? "border-red-300 dark:border-red-700 focus:ring-red-200 dark:focus:ring-red-600 focus:border-red-400 dark:focus:border-red-600"
                      : "border-gray-300 dark:border-slate-700 focus:ring-indigo-300 dark:focus:ring-indigo-500 focus:border-indigo-400 dark:focus:border-indigo-500"
                  }`}
                placeholder="Re-enter password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPw((s) => !s)}
                className="absolute inset-y-0 right-0 px-3 sm:px-4 text-xs sm:text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                aria-label={showConfirmPw ? "Hide password" : "Show password"}
              >
                {showConfirmPw ? "Hide" : "Show"}
              </button>
            </div>
            {matchState === "mismatch" && (
              <p className="mt-1 text-[11px] sm:text-xs text-red-600 dark:text-red-400">
                Passwords don’t match.
              </p>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={!canSubmit}
            className={`w-full text-white text-sm sm:text-base font-semibold py-2.5 sm:py-3 rounded-lg transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 dark:focus-visible:ring-indigo-500
              ${
                canSubmit
                  ? "bg-indigo-700 hover:bg-indigo-800 dark:bg-indigo-600 dark:hover:bg-indigo-700"
                  : "bg-indigo-400 dark:bg-indigo-500 cursor-not-allowed"
              }`}
          >
            {isSubmitting ? "Resetting…" : "Reset Password"}
          </button>
        </form>

        <div className="mt-4 text-center text-xs sm:text-sm">
          <Link
            to="/login"
            className="text-indigo-700 dark:text-indigo-400 hover:underline"
          >
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}
